import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    TextField,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    Paper
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

interface IPersonalQuestion {
    _id: string;
    id: string;
    text: string;
    answer?: string;
}

interface IPersonalQuestionnaire {
    _id: string;
    lawyerId: string | { _id: string; name: string; email: string };
    assigneeEmail: string;
    questions: IPersonalQuestion[];
    status: 'PENDING' | 'COMPLETED';
    assignedAt: string;
    completedAt?: string;
    token: string;
}

const PersonalQuestionnaires: React.FC = () => {
    const { user } = useAuthStore();
    const queryClient = useQueryClient();
    const [openCreate, setOpenCreate] = useState(false);

    // Create Form State
    const [assigneeEmail, setAssigneeEmail] = useState('');
    interface NewQuestion { text: string; key: string; }
    const [questions, setQuestions] = useState<NewQuestion[]>([{ text: '', key: '' }]);

    // Fetch Assignments
    const { data: assignments = [], isLoading } = useQuery<IPersonalQuestionnaire[]>({
        queryKey: ['personalQuestionnaires'],
        queryFn: async () => {
            const res = await api.get('/personal-questionnaires');
            return res.data;
        }
    });

    // Create Mutation
    const createMutation = useMutation({
        mutationFn: async (data: { assigneeEmail: string, questions: NewQuestion[] }) => {
            return api.post('/personal-questionnaires', data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personalQuestionnaires'] });
            setOpenCreate(false);
            setAssigneeEmail('');
            setQuestions([{ text: '', key: '' }]);
        }
    });

    // Submit Answer Mutation
    const submitAnswerMutation = useMutation({
        mutationFn: async (data: { id: string, answers: Record<string, string> }) => {
            return api.post(`/personal-questionnaires/${data.id}/submit`, { answers: data.answers });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['personalQuestionnaires'] });
        }
    });

    const handleAddQuestion = () => {
        setQuestions([...questions, { text: '', key: '' }]);
    };

    const handleQuestionChange = (index: number, field: 'text' | 'key', value: string) => {
        const newQuestions = [...questions];
        const currentQuestion = newQuestions[index];

        if (field === 'text') {
            const cleanText = (t: string) => t.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
            const baseKey = cleanText(currentQuestion.text);
            // Check if current key matches either old format (just snake case) or new format (personal_ prefix)
            // This ensures we keep auto-updating if the user hasn't manually customized the key
            const isAutoKey = !currentQuestion.key ||
                currentQuestion.key === baseKey ||
                currentQuestion.key === `personal_${baseKey}`;

            const newBaseKey = cleanText(value);
            // Always use the new format with personal_ prefix
            const newAutoKey = `personal_${newBaseKey}`;

            if (isAutoKey) {
                newQuestions[index] = { ...currentQuestion, text: value, key: newAutoKey };
            } else {
                newQuestions[index] = { ...currentQuestion, text: value };
            }
        } else {
            newQuestions[index] = { ...currentQuestion, key: value };
        }
        setQuestions(newQuestions);
    };

    const handleRemoveQuestion = (index: number) => {
        const newQuestions = questions.filter((_, i) => i !== index);
        setQuestions(newQuestions);
    };

    const handleCreateSubmit = () => {
        const filteredQuestions = questions.filter(q => q.text.trim() !== '');
        if (!assigneeEmail || filteredQuestions.length === 0) return;
        createMutation.mutate({ assigneeEmail, questions: filteredQuestions });
    };

    // Answering View State
    const [selectedAssignment, setSelectedAssignment] = useState<IPersonalQuestionnaire | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});

    const handleOpenAssignment = (assignment: IPersonalQuestionnaire) => {
        setSelectedAssignment(assignment);
        const initialAnswers: Record<string, string> = {};
        assignment.questions.forEach(q => {
            initialAnswers[q.id] = q.answer || '';
        });
        setAnswers(initialAnswers);
    };

    const handleSubmitAnswers = () => {
        if (!selectedAssignment) return;
        submitAnswerMutation.mutate({ id: selectedAssignment._id, answers });
        setSelectedAssignment(null);
    };

    const isLawyer = user?.userType === 'EL';

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Personal Questions
                </Typography>
                {isLawyer && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenCreate(true)}
                        sx={{ borderRadius: 2 }}
                    >
                        Assign Questions
                    </Button>
                )}
            </Box>

            {/* Tabs if needed? Actually simple list is fine. */}
            {/* If lawyer, show "Assigned to others". If user, show "Assigned to me". */}

            {isLoading ? (
                <CircularProgress />
            ) : (
                <Grid container spacing={3}>
                    {assignments.length === 0 && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'transparent', boxShadow: 'none' }}>
                                <Typography color="textSecondary">No questionnaires found.</Typography>
                            </Paper>
                        </Grid>
                    )}
                    {assignments.map((assignment) => (
                        <Grid item xs={12} md={6} lg={4} key={assignment._id}>
                            <Card sx={{
                                borderRadius: 3,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                transition: 'transform 0.2s',
                                '&:hover': { transform: 'translateY(-4px)' },
                                borderLeft: `6px solid ${assignment.status === 'COMPLETED' ? '#4caf50' : '#ff9800'}`
                            }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Chip
                                            label={assignment.status}
                                            color={assignment.status === 'COMPLETED' ? 'success' : 'warning'}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Typography variant="caption" color="textSecondary">
                                            {new Date(assignment.assignedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}
                                        </Typography>
                                    </Box>

                                    {isLawyer ? (
                                        <>
                                            <Typography variant="h6" gutterBottom>
                                                To: {assignment.assigneeEmail}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                {assignment.questions.length} Questions
                                            </Typography>
                                        </>
                                    ) : (
                                        <>
                                            <Typography variant="h6" gutterBottom>
                                                From: {typeof assignment.lawyerId === 'object' ? assignment.lawyerId.name : 'Lawyer'}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                                {assignment.questions.length} Questions
                                            </Typography>
                                        </>
                                    )}

                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            onClick={() => handleOpenAssignment(assignment)}
                                        >
                                            {isLawyer ? 'View Details' : (assignment.status === 'COMPLETED' ? 'View Answers' : 'Answer Now')}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Create Dialog */}
            <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Personal Questions</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Assignee Email"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={assigneeEmail}
                        onChange={(e) => setAssigneeEmail(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Typography variant="subtitle1" gutterBottom>Questions</Typography>
                    {questions.map((q, index) => (
                        <Box key={index} sx={{ mb: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TextField
                                    fullWidth
                                    label={`Question ${index + 1}`}
                                    placeholder="e.g. What is your pet's name?"
                                    value={q.text}
                                    onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                                    size="small"
                                />
                                <IconButton onClick={() => handleRemoveQuestion(index)} disabled={questions.length === 1} size="small" sx={{ ml: 1 }}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                        </Box>
                    ))}
                    <Button startIcon={<AddIcon />} onClick={handleAddQuestion} size="small">
                        Add Question
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
                    <Button onClick={handleCreateSubmit} variant="contained" disabled={createMutation.isPending}>
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View/Answer Dialog */}
            <Dialog open={!!selectedAssignment} onClose={() => setSelectedAssignment(null)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isLawyer ?
                        `Details (Assignee: ${selectedAssignment?.assigneeEmail})` :
                        `Personal Questionnaire from ${typeof selectedAssignment?.lawyerId === 'object' ? selectedAssignment?.lawyerId.name : 'Lawyer'}`
                    }
                </DialogTitle>
                <DialogContent dividers>
                    <List>
                        {selectedAssignment?.questions.map((q, idx) => (
                            <Box key={q.id} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    {idx + 1}. {q.text}
                                </Typography>
                                {isLawyer || selectedAssignment.status === 'COMPLETED' ? (
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                                        <Typography variant="body1">
                                            {isLawyer ? (q.answer || 'Not answered yet') : (answers[q.id] || q.answer || 'No answer')}
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        placeholder="Your answer..."
                                        value={answers[q.id] || ''}
                                        onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })}
                                    />
                                )}
                            </Box>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedAssignment(null)}>Close</Button>
                    {!isLawyer && selectedAssignment?.status !== 'COMPLETED' && (
                        <Button variant="contained" onClick={handleSubmitAnswers} disabled={submitAnswerMutation.isPending}>
                            Submit Answers
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PersonalQuestionnaires;
